/**
 * Device-independent cancellation of a kolula KEY subscription (§ 312k BGB):
 * Infos → login with an e-mail code → manage page. Plan:
 * `docs/plans/kolula-key-subscription-b2c-obligations.md`, PR 5.
 *
 * The login and verify pages are the existing Flow 3 pages; `intent=manage`
 * in the URL tells them to end on the manage page instead of the service
 * selection. The agreement id itself never goes into the URL for this page —
 * it lives in Redux, and a refresh without it starts the login again.
 */

export const SUBSCRIPTION_MANAGE_INTENT = "manage";

/** True when the URL asks the login → verify path to end on the manage page. */
export const isManageIntent = (search: string): boolean =>
  new URLSearchParams(search.startsWith("?") ? search.substring(1) : search).get(
    "intent"
  ) === SUBSCRIPTION_MANAGE_INTENT;

/** The search string that starts the manage flow on the login page. */
export const manageIntentSearch = `?intent=${SUBSCRIPTION_MANAGE_INTENT}`;

/**
 * Why the manage page has no subscription to show. Each cause gets its own
 * message — a failed load must never read as "nothing there".
 * - `not_found`: the backend answered, but not with this agreement (404, or an
 *   empty list, which is how `GET /customer-agreements/:ids` says "unknown id");
 * - `network`: no response at all (offline, timeout, CORS);
 * - `server`: any other error status.
 */
export type SubscriptionLoadFailure = "not_found" | "network" | "server";

const statusOf = (error: unknown): unknown =>
  error && typeof error === "object" ? (error as { status?: unknown }).status : undefined;

export const classifySubscriptionLoadError = (
  error: unknown
): SubscriptionLoadFailure => {
  const status = statusOf(error);
  if (status === 404) return "not_found";
  if (typeof status !== "number") return "network";
  return "server";
};

/** The server's own message behind a failure, when it sent one. */
export const serverMessageOf = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const data = (error as { data?: unknown }).data;
  if (data && typeof data === "object") {
    const message = (data as { message?: unknown }).message;
    return typeof message === "string" && message.trim() ? message : undefined;
  }
  return undefined;
};

/** The cancel route's refusal code when the withdrawal period has ended. */
export const WITHDRAWAL_PERIOD_OVER = "withdrawal_period_over";

/**
 * The message to show when a withdrawal fails: the known refusal in the
 * user's language, else the server's own message, else the generic text.
 * The route's English `message` is never shown for a refusal we can name.
 */
export const withdrawErrorMessage = (
  error: unknown,
  t: (key: string) => string
): string => {
  const data =
    error && typeof error === "object" ? (error as { data?: unknown }).data : undefined;
  const code =
    data && typeof data === "object" ? (data as { code?: unknown }).code : undefined;
  if (code === WITHDRAWAL_PERIOD_OVER) {
    return t("subscriptions.withdraw_dialog.period_over");
  }
  return serverMessageOf(error) ?? t("subscriptions.withdraw_dialog.error_message");
};
